import {NullableStation, Station} from "../stations/station.service.interface";
import {IFlightSearchService,} from "./flight-search.service.interface";
import {IServiceFactory} from "../service-factory.interface";
import {ServiceBase} from "../service-base";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {FlightSearchControllerModel} from "./flight-search-controller/flight-search-controller.model";
import {RoundTripLowFareReaderFactory} from "../low-fare/round-trip-low-fare-reader-factory";
import {IRoundTripLowFareReader} from "../low-fare/low-fare-readers/low-fare-reader.interface";
import {BookingEngineUrlBuilder} from "./booking-engine-url-builder";
import {TimeSpan} from "../../types/time-span";
import {WellKnownQueryParams} from "../window/window.service.interface";
import {
    SpecialPriceMarketUserAgreement,
    SpecialPriceMarketUserOptions
} from "./flight-search-controller/flight-search-params.interface";
import {buildFullUrl} from "../http/http-helpers";

export class FlightSearchService extends ServiceBase implements IFlightSearchService {
    constructor(services: IServiceFactory) {
        super(services);

        this.searchController = new FlightSearchControllerModel(this.services, 'flightSearch.searchControllerStorageKey');
        this._lowFareReaderFactory = new RoundTripLowFareReaderFactory(
            this.services,
            this.searchController,
            true,
            () => this.services.currency.current);


    }

    readonly searchController: FlightSearchControllerModel;

    private readonly _lowFareReaderFactory: RoundTripLowFareReaderFactory;
    getLowFareReader(): IRoundTripLowFareReader {
        return this._lowFareReaderFactory.lowFareReader;

    }

    private _getRecentlySearch(key: LocalStorageKeys): Station[] {
        let result: Station[] = [];
        const savedStations = this.services.localStorage.getJson<string[]>(key) || [];

        for(let stationCode of savedStations) {
            const s = this.services.stations.tryGetStation(stationCode);
            if(s) {
                result.push(s);
            }
        }

        return result;
    }

    getRecentlySearchedOrigins(): Station[] {
        return this._getRecentlySearch('flightSearch.recentlySearchedOriginStations');
    }
        
    getRecentlySearchedDestinations(): Station[] {
        return this._getRecentlySearch('flightSearch.recentlySearchedDestinationStations');
    }        
        
    private _saveRecentlySearch(key: LocalStorageKeys, station: NullableStation) {
        if(!station) {
            return;
        }
        let recentlySearch = this.services.localStorage.getJson<string[]>(key) || [];        
        const stationIndex = recentlySearch.indexOf(station.stationCode);

        if (stationIndex >= 0) {
            recentlySearch.splice(stationIndex, 1);
        }

        recentlySearch.splice(0, 0, station.stationCode); 

        recentlySearch = recentlySearch.slice(0, 3);

        this.services.localStorage.setJson(key, recentlySearch);
    }

    private _getBookingEngineSearchUrl(): string {
        const bookingEngineUrl = this.services.configuration.data.bookingEngineUrl?.toLowerCase();
        if(!bookingEngineUrl) {
            throw new Error('bookingEngineUrl was not configured');
        }

        if(bookingEngineUrl.endsWith('deeplink/search')) {
            return bookingEngineUrl;
        }

        return buildFullUrl(bookingEngineUrl, 'deeplink/search');
    }
    async startBooking(): Promise<void> {

        const fieldsWithError = this.searchController.activateErrorsValidation();
        if(fieldsWithError.length > 0) {
            return;
        }

        this._saveRecentlySearch('flightSearch.recentlySearchedOriginStations', this.searchController.departureOrigin);
        this._saveRecentlySearch('flightSearch.recentlySearchedDestinationStations', this.searchController.departureDestination);

        const bookingEngineUrl = this._getBookingEngineSearchUrl();

        const urlBuilder = new BookingEngineUrlBuilder();
        if(this.searchController.isOneWay) {
            urlBuilder.isSevenDaysSearch = false;
        } else {
            urlBuilder.isSevenDaysSearch = (7 <= TimeSpan.fromMilliseconds((this.searchController.returnDate?.getTime() ?? 0) - (this.searchController.departureDate?.getTime() ?? 0))
                                                         .totalDays);
        }

        urlBuilder.isRoundTrip = !this.searchController.isOneWay;
        urlBuilder.origin1 = this.searchController.departureOrigin?.stationMacCode ?? "";
        urlBuilder.destination1 = this.searchController.departureDestination?.stationMacCode ?? "";
        urlBuilder.departureDate1 = this.services.time.formatYYY_MM_DD(this.searchController.departureDate);


        urlBuilder.specialPriceMarketUserOption = this.searchController.fields.specialPriceMarketUserOption.value ?? SpecialPriceMarketUserOptions.None;
        urlBuilder.specialPriceMarketUserAgreement = this.searchController.fields.specialPriceMarketUserAgreement.value ?? SpecialPriceMarketUserAgreement.None;
        if(!this.searchController.isOneWay) {
            urlBuilder.returnDate1 = this.services.time.formatYYY_MM_DD(this.searchController.returnDate);
        }

        urlBuilder.passengersTypes = this.searchController.passengers;

        urlBuilder.culture = this.services.language.currentLanguage;
        urlBuilder.bookingCurrency = this.services.currency.current;


        urlBuilder.agent = (this.services.window.getUrlParam(WellKnownQueryParams.Agent) ?? "");

        const specialPriceMarket = this.searchController.getSpecialPriceMarket();

        if(specialPriceMarket && (this.searchController.fields.specialPriceMarketUserOption.value !== SpecialPriceMarketUserOptions.None)) {

            if(urlBuilder.agent) {
                urlBuilder.originalAgent = urlBuilder.agent;
            }

            const specialPriceAgent = specialPriceMarket.getAgent(this.searchController.fields.specialPriceMarketUserOption.value  === SpecialPriceMarketUserOptions.Season);

            if(specialPriceAgent) {
                urlBuilder.agent = specialPriceAgent;
            }

            const url = urlBuilder.buildUrl(this.services.window.origin);
            url.pathname = this.services.navigation.applicationRoutes.specialPricesMarket.paths[0];
            this.services.window.searchParams.forEach((value, name) => {
                if(name.toLowerCase() !== WellKnownQueryParams.Agent) {
                    url.searchParams.append(name, value);
                }
            });

            this.services.window.redirect(url.href);
        } else {
            const url = urlBuilder.buildUrl(bookingEngineUrl);
            this.services.window.redirect(url.href);
        }
    }

    switchStations() {
        let tempStation = this.searchController.departureOrigin;
        let departureDate = this.searchController.departureDate;
        let returnDate = this.searchController.returnDate;
        this.searchController.departureOrigin = this.searchController.departureDestination;
        this.searchController.departureDestination = tempStation;
        this.searchController.departureDate = departureDate;
        this.searchController.returnDate = returnDate;
    }


    continueWithSpecialPrices(): void {
        const newSearchParams = new URLSearchParams();
        this.services.window.searchParams.forEach((value, name) => {
            if(name.toLowerCase() !== WellKnownQueryParams.OriginalAgent.toLowerCase()) {
                newSearchParams.append(name, value);
            }
        });

        newSearchParams.append(WellKnownQueryParams.SpecialPriceUserAgreement, SpecialPriceMarketUserAgreement.Agree.toString());

        const url = new URL(this._getBookingEngineSearchUrl());
        url.search = newSearchParams.toString();
        this.services.window.redirect(url.href);
    }


    continueWithoutSpecialPrices(): void {
        const newSearchParams = new URLSearchParams();
        const paramsToIgnore: string[] = [
            WellKnownQueryParams.Agent.toLowerCase(),
            WellKnownQueryParams.SpecialPriceUserOption.toLowerCase(),
            WellKnownQueryParams.SpecialPriceUserAgreement.toLowerCase()
        ]
        this.services.window.searchParams.forEach((value, name) => {
            if(name.toLowerCase() === WellKnownQueryParams.OriginalAgent.toLowerCase()) {
                newSearchParams.append(WellKnownQueryParams.Agent, value);
            } else if(!paramsToIgnore.includes(name.toLowerCase())) {
                newSearchParams.append(name, value);
            }


        });


        const url = new URL(this._getBookingEngineSearchUrl());
        url.search = newSearchParams.toString();
        this.services.window.redirect(url.href);
    }
}
